import { useState } from 'react';
import {
  AlertCircle,
  AlignLeft,
  Building2,
  Calendar,
  ChevronRight,
  CornerDownRight,
  DollarSign,
  GripVertical,
  Hash,
  Link as LinkIcon,
  ListChecks,
  Loader2,
  Mail,
  MousePointerClick,
  Pencil,
  PenLine,
  Save,
  Sparkles,
  Trash2,
  Type,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui-kit/button';
import { useDocxSelection, type MirrorEntry, type RenderRow } from './context';
import { COMPANY_SUB_ICON, SUB_ICON, type ScalarType } from './types';

const TYPE_ICON: Record<ScalarType, LucideIcon> = {
  text: Type,
  textarea: AlignLeft,
  number: Hash,
  date: Calendar,
  currency: DollarSign,
  select: ListChecks,
  email: Mail,
  url: LinkIcon,
};

function rowId(row: RenderRow): string {
  if (row.kind === 'field') return `field:${row.v.name}`;
  return `${row.kind}:${row.meta.key}`;
}

/**
 * Marks an AI suggestion the user has not touched. `commitDraft` rebuilds an edited field
 * without `origin`, so the badge clears itself the moment they make the field their own — it
 * reads as "review this", not as a permanent label.
 */
function AiBadge() {
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full bg-selise-blue/10 px-1.5 py-0.5 text-[10px] font-medium text-selise-blue shrink-0"
      title="Suggested by AI — edit or remove it"
    >
      <Sparkles className="w-2.5 h-2.5" />
      AI
    </span>
  );
}

export function VariableList() {
  const {
    renderRows,
    isSaving,
    warnings,
    reorderRows,
    save,
    canSuggest,
    suggestionError,
    suggestFields,
    isDocumentReady,
  } = useDocxSelection();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorderRows(String(active.id), String(over.id));
    }
  };

  const isEmpty = renderRows.length === 0;
  const rowIds = renderRows.map(rowId);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border shrink-0">
        <h2 className="text-sm font-semibold text-[hsl(var(--fg))]">Marked fields</h2>
        <p className="text-xs text-globe-grey mt-0.5">
          Select text in the document to turn it into a fill-in field.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border p-8 text-center">
            {/* The AI offer lives here rather than in the header because it is only ever
                available while this empty state is showing — see `canSuggest`. */}
            {canSuggest ? (
              <>
                <Sparkles className="h-8 w-8 text-selise-blue" />
                <p className="text-sm text-[hsl(var(--fg))]">
                  Let AI find the fill-in fields for you.
                </p>
                <Button type="button" className="w-full gap-2" onClick={suggestFields}>
                  <Sparkles className="h-4 w-4" />
                  Suggest fields with AI
                </Button>
                {suggestionError && (
                  <p className="text-xs text-crimson bg-crimson/10 rounded-md p-2 w-full">
                    {suggestionError}
                  </p>
                )}
                <p className="text-xs text-globe-grey">
                  Or select text in the document to mark a field yourself.
                </p>
              </>
            ) : (
              <>
                <MousePointerClick className="h-8 w-8 text-globe-grey" />
                <p className="text-sm text-globe-grey">
                  No fields yet. Select text in the document to turn it into a fill-in field.
                </p>
              </>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1.5">
                {renderRows.map((row) => (
                  <SortableRow key={rowId(row)} id={rowId(row)} row={row} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Save bar */}
      <div className="shrink-0 px-4 py-3 border-t border-border space-y-2">
        {warnings.length > 0 && (
          <div className="text-[11px] text-amber-600 space-y-0.5">
            {warnings.map((w, i) => (
              <p key={i} className="flex items-start gap-1.5">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                {w}
              </p>
            ))}
          </div>
        )}
        <Button
          onClick={save}
          // Saving tokenizes the .docx, so it needs the bytes. The list renders before the
          // download finishes now, and save() would otherwise return silently.
          disabled={isSaving || isEmpty || !isDocumentReady}
          variant="primary"
          className="w-full gap-2"
        >
          {isSaving || !isDocumentReady ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving ? 'Saving…' : !isDocumentReady ? 'Loading document…' : 'Save and Continue'}
        </Button>
      </div>
    </div>
  );
}

/** Nested list of mirror (valueCopyFrom) fields shown under their source leaf. Not draggable. */
function MirrorList({ mirrors, pl }: { mirrors: MirrorEntry[]; pl: string }) {
  const { fieldErrors, activeHighlight, startEdit, removeVariable, scrollToField } =
    useDocxSelection();
  return (
    <ul className="border-t border-border bg-muted/20">
      {mirrors.map((m) => {
        const Icon = TYPE_ICON[m.v.type] ?? Type;
        const err = fieldErrors[m.v.name];
        const active = activeHighlight === m.v.name;
        return (
          <li key={m.v.name}>
            <div
              className={`group flex items-center gap-2 ${pl} pr-2.5 py-1.5 transition-colors cursor-pointer ${
                active ? 'bg-selise-blue/5' : 'hover:bg-muted'
              }`}
              onClick={() => {
                scrollToField(m.v.name);
                startEdit(m.index);
              }}
            >
              <CornerDownRight className="w-3 h-3 text-globe-grey shrink-0" />
              <Icon className="w-3.5 h-3.5 text-globe-grey shrink-0" />
              <p className="text-[13px] text-[hsl(var(--fg))] truncate flex-1">{m.v.label}</p>
              <span className="text-[10px] text-globe-grey shrink-0">mirror</span>
              {err && <AlertCircle className="w-4 h-4 text-crimson shrink-0" />}
              <RowActions
                onEdit={() => startEdit(m.index)}
                onRemove={() => removeVariable(m.index)}
              />
            </div>
            {err && <p className={`${pl} pb-1 text-[11px] text-crimson`}>{err}</p>}
          </li>
        );
      })}
    </ul>
  );
}

/** A draggable top-level row (a plain field or a whole person group). */
function SortableRow({ id, row }: { id: string; row: RenderRow }) {
  const {
    fieldErrors,
    activeHighlight,
    startEdit,
    removeVariable,
    removePerson,
    removeCompany,
    scrollToField,
  } = useDocxSelection();

  // Which leaves have their mirror group expanded (collapsed by default).
  const [openMirrors, setOpenMirrors] = useState<Set<string>>(new Set());
  const toggleMirrors = (name: string) =>
    setOpenMirrors((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing touch-none p-0.5 text-globe-grey opacity-40 group-hover:opacity-100 transition-opacity shrink-0"
      aria-label="Drag to reorder"
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical className="w-3.5 h-3.5" />
    </button>
  );

  // A chevron toggle when a leaf has mirror children, else a spacer to keep rows aligned.
  const mirrorToggle = (name: string, count: number) =>
    count > 0 ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleMirrors(name);
        }}
        aria-expanded={openMirrors.has(name)}
        aria-label={openMirrors.has(name) ? 'Collapse mirrors' : 'Expand mirrors'}
        className="p-0.5 text-globe-grey shrink-0"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform ${openMirrors.has(name) ? 'rotate-90' : ''}`}
        />
      </button>
    ) : (
      <span className="w-4.5 shrink-0" />
    );

  if (row.kind === 'field') {
    const Icon = TYPE_ICON[row.v.type] ?? Type;
    const err = fieldErrors[row.v.name];
    const active = activeHighlight === row.v.name;
    const mirrorCount = row.mirrors.length;
    return (
      <li ref={setNodeRef} style={style}>
        <div
          className={`rounded-lg border overflow-hidden ${
            active ? 'border-selise-blue' : 'border-border'
          }`}
        >
          <div
            className={`group flex items-center gap-2 px-2.5 py-2 transition-colors cursor-pointer ${
              active ? 'bg-selise-blue/5' : 'hover:bg-muted'
            }`}
            onClick={() => {
              scrollToField(row.v.name);
              startEdit(row.index);
            }}
          >
            {handle}
            {mirrorToggle(row.v.name, mirrorCount)}
            <div className="w-6 h-6 rounded bg-selise-blue/10 flex items-center justify-center shrink-0">
              <Icon className="w-3.5 h-3.5 text-selise-blue" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="text-sm text-[hsl(var(--fg))] truncate">{row.v.label}</p>
                {row.v.origin === 'ai' && <AiBadge />}
              </div>
              <p className="text-[11px] text-globe-grey truncate">
                {`${row.v.required ? 'Required · ' : ''}${row.v.type}`}
                {mirrorCount > 0 && ` · ${mirrorCount} mirror${mirrorCount === 1 ? '' : 's'}`}
              </p>
            </div>
            {err && <AlertCircle className="w-4 h-4 text-crimson shrink-0" />}
            <RowActions
              onEdit={() => startEdit(row.index)}
              onRemove={() => removeVariable(row.index)}
            />
          </div>
          {mirrorCount > 0 && openMirrors.has(row.v.name) && (
            <MirrorList mirrors={row.mirrors} pl="pl-9" />
          )}
        </div>
        {err && <p className="px-2.5 pt-1 text-[11px] text-crimson">{err}</p>}
      </li>
    );
  }

  // Person / company group — same shell, different header and child icons.
  const isCompany = row.kind === 'company';
  return (
    <li ref={setNodeRef} style={style} className="rounded-lg border border-border overflow-hidden">
      <div className="group flex items-center gap-2 bg-muted/50 px-2.5 py-2">
        {handle}
        <div className="w-6 h-6 rounded bg-selise-blue/10 flex items-center justify-center shrink-0">
          {isCompany ? (
            <Building2 className="w-3.5 h-3.5 text-selise-blue" />
          ) : (
            <UserRound className="w-3.5 h-3.5 text-selise-blue" />
          )}
        </div>
        <p className="text-sm font-medium text-[hsl(var(--fg))] truncate">{row.meta.label}</p>
        {/* One badge for the whole group — a pill on every child would be noise. */}
        {row.children.some((c) => c.v.origin === 'ai') && <AiBadge />}
        {row.kind === 'person' && row.meta.isSignatory && (
          <span className="inline-flex items-center gap-1 rounded-full bg-selise-blue/10 px-1.5 py-0.5 text-[10px] font-medium text-selise-blue shrink-0">
            <PenLine className="w-2.5 h-2.5" />
            signs · {row.meta.signingOrder ?? 1}
          </span>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() =>
            isCompany ? removeCompany(row.meta.key) : removePerson(row.meta.key)
          }
          className="p-1 rounded-md text-globe-grey hover:text-crimson hover:bg-muted transition-colors"
          aria-label={`Remove ${isCompany ? 'company' : 'person'} ${row.meta.label}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <ul className="divide-y divide-border">
        {row.children.map((c) => {
          const Icon =
            (c.v.companySubKind && COMPANY_SUB_ICON[c.v.companySubKind]) ||
            (c.v.subKind && SUB_ICON[c.v.subKind]) ||
            Type;
          const err = fieldErrors[c.v.name];
          const active = activeHighlight === c.v.name;
          const mirrorCount = c.mirrors.length;
          return (
            <li key={c.v.name}>
              <div
                className={`group flex items-center gap-2 px-2.5 py-2 pl-4 transition-colors cursor-pointer ${
                  active ? 'bg-selise-blue/5' : 'hover:bg-muted'
                }`}
                onClick={() => {
                  scrollToField(c.v.name);
                  startEdit(c.index);
                }}
              >
                {mirrorToggle(c.v.name, mirrorCount)}
                <Icon className="w-3.5 h-3.5 text-globe-grey shrink-0" />
                <p className="text-sm text-[hsl(var(--fg))] truncate flex-1">{c.v.label}</p>
                {row.kind === 'person' && c.v.subKind === 'company' && row.meta.companyRef && (
                  <span className="text-[10px] text-selise-blue shrink-0">shared</span>
                )}
                {err && <AlertCircle className="w-4 h-4 text-crimson shrink-0" />}
                <RowActions
                  onEdit={() => startEdit(c.index)}
                  onRemove={() => removeVariable(c.index)}
                />
              </div>
              {err && <p className="px-2.5 pb-1 pl-4 text-[11px] text-crimson">{err}</p>}
              {mirrorCount > 0 && openMirrors.has(c.v.name) && (
                <MirrorList mirrors={c.mirrors} pl="pl-11" />
              )}
            </li>
          );
        })}
      </ul>
    </li>
  );
}

function RowActions({ onEdit, onRemove }: { onEdit: () => void; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="p-1 rounded-md text-globe-grey hover:text-selise-blue hover:bg-muted transition-colors"
        aria-label="Edit"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-1 rounded-md text-globe-grey hover:text-crimson hover:bg-muted transition-colors"
        aria-label="Remove"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
